import React, { useState, useEffect } from "react";
import axios from "axios";

import DetailPolicy from "./detailComponents/DetailPolicy";
import { SingleButton } from "../../../assets/components/ButtonGroup";
import SectionTitle from "../../../assets/components/TitleGroup";
import checkOn from "../../../assets/icons/Select/check_checked.svg";
import checkOff from "../../../assets/icons/Select/check_unchecked.svg";
import { BottomWithButton } from "../../../assets/components/BottomSheetGroup";

const API = process.env.REACT_APP_FASTAPI_ADS_URL;

const AdsRegistStep2 = ({ setIsNextEnabled, onNext }) => {
    const terms = [
        { id: "tos", label: "(필수) 서비스 이용약관 동의", required: true, showDetail: true },
        { id: "privacy", label: "(필수) 개인정보 수집 및 이용 동의", required: true, showDetail: true },
        { id: "age", label: "(필수) 만 18세 이상입니다", required: true, showDetail: false },
        { id: "marketing", label: "(선택) 마케팅 정보 수신 동의", required: false, showDetail: false },
    ];

    const userId = localStorage.getItem("user_id");

    // 개별 동의 상태
    const [checks, setChecks] = useState(() =>
        Object.fromEntries(terms.map((t) => [t.id, false]))
    );

    // 전체 동의 상태(표시/토글 용)
    const [agreeAll, setAgreeAll] = useState(false);

    // 정책 보기 모달 상태
    const [policyOpen, setPolicyOpen] = useState(false);
    const [policyType, setPolicyType] = useState("tos"); // 'tos' | 'privacy'

    // 개별 토글
    const toggleOne = (id) => {
        setChecks((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // 전체 동의 토글
    const handleAgreeAll = (e) => {
        const next = e.target.checked;
        setAgreeAll(next);
        setChecks(() => Object.fromEntries(terms.map((t) => [t.id, next])));
    };

    // 개별 상태 변화 시, 전체 동의/다음 버튼 활성화 동기화
    useEffect(() => {
        const all = terms.every((t) => checks[t.id]);
        const requiredOk = terms.filter((t) => t.required).every((t) => checks[t.id]);
        if (agreeAll !== all) setAgreeAll(all); // 표시 상태만 동기화
        setIsNextEnabled(requiredOk);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checks]);

    const canProceed = terms.filter((t) => t.required).every((t) => checks[t.id]);

    // 약관 바텀시트에서 "확인" 눌렀을 때: 해당 약관 체크 + 시트 닫기
    const handlePolicyConfirm = () => {
        setChecks((prev) => ({
            ...prev,
            [policyType]: true, // 'tos' 또는 'privacy'
        }));
        setPolicyOpen(false);
    };

    const handleNextClick = async () => {
        if (!canProceed) return;

        const marketingChecked = checks["marketing"];

        if (userId) {
            try { //추가로 status : agreed로 수정
                await axios.post(`${API}/ads/agreed/terms`, {
                    opt: marketingChecked ? 1 : 0,   // 체크 → 1, 체크 X → 0
                    user_id: userId,
                    user_status: "agreed"
                });
            } catch (e) {
                console.error(e);
                // 실패해도 화면 흐름은 진행
            }
        }

        onNext();
    };


    return (
        <div className="flex flex-col h-dvh">
            <div className="p-[16px]">
                <SectionTitle
                    title={"위즈마켓 회원가입하기"}
                    subTitle={"회원가입을 위해 아래 필수 약관에 동의해 주세요."}
                    className={"pb-[40px]"}
                />

                <div className="space-y-[12px]">
                    {/* 전체 동의 */}
                    <div className="flex items-center">
                        <label
                            htmlFor="agreeAll"
                            className="flex items-center gap-[8px] py-[8px] cursor-pointer"
                        >
                            {/* 실제 체크박스는 숨기고, 아이콘으로만 표시 */}
                            <input
                                type="checkbox"
                                id="agreeAll"
                                className="hidden"
                                checked={agreeAll}
                                onChange={handleAgreeAll}
                            />
                            <img
                                src={agreeAll ? checkOn : checkOff}
                                alt={agreeAll ? "전체 동의: 켜짐" : "전체 동의: 꺼짐"}
                                className="w-[24px] h-[24px]"
                            />
                            <span className="text-b1 text-text-subtle">전체 동의</span>
                        </label>
                    </div>

                    <div className="w-full border-t border-border-default opacity-30" />
                    {/* 약관 항목 리스트 */}
                    {terms.map((t) => {
                        const checked = checks[t.id];
                        return (
                            <div
                                key={t.id}
                                className="flex items-center justify-between w-full "
                            >
                                {/* 좌측: 아이콘 + 텍스트(행 전체 클릭 시 토글) */}
                                <button
                                    type="button"
                                    className="flex items-start gap-[8px] py-[8px] flex-1 min-w-0 text-left"
                                    onClick={() => toggleOne(t.id)}
                                >
                                    <img
                                        src={checked ? checkOn : checkOff}
                                        alt={checked ? "동의됨" : "동의 안 됨"}
                                        className="w-[24px] h-[24px]"
                                    />
                                    <p
                                        className={`text-b1 text-text-subtle break-keep`}
                                    >
                                        {t.label}
                                    </p>
                                </button>

                                {/* 우측: 자세히 (필요한 항목만) */}
                                {t.showDetail && (
                                    <button
                                        className="text-c2 text-text-primary"
                                        onClick={() => {
                                            setPolicyType(t.id); // 'tos' 또는 'privacy'
                                            setPolicyOpen(true);
                                        }}
                                    >
                                        자세히
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-surface-default">
                <SingleButton onClick={handleNextClick} disabled={!canProceed}>
                    다음
                </SingleButton>
            </div>

            <BottomWithButton
                isOpen={policyOpen}
                onClose={() => setPolicyOpen(false)}
                topOffset={"60px"}
                title={policyType === "tos" ? "서비스 이용 약관 동의" : "개인정보 수집 및 이용 동의"}
                subtitle=""
                buttonLabel="확인"
                onSubmit={handlePolicyConfirm}
                buttonVariant="fill"
            >
                <div className="text-b2 text-text-subtler [&_*]:text-inherit [&_*]:font-normal">
                    <DetailPolicy type={policyType} />
                </div>
            </BottomWithButton>

        </div>
    );
};

export default AdsRegistStep2;
